import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01,
    Copy01,
    SearchLg,
    FileCheck02,
    FileCode01
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { useTranslation } from 'react-i18next';
import { useDocumentSchemas, useCreateDocumentSchema, useUpdateDocumentSchema, useDeleteDocumentSchema } from '@/hooks/api/use-document-schemas';
import { useAddAttributeToSchema } from '@/hooks/api/use-schema-attributes';
import { useDocumentType } from '@/hooks/api/use-document-types';
import { apiGet } from '@/lib/api-client';
import type { DocumentSchemaResponse, DocumentSchemaCreate, DocumentSchemaUpdate, SchemaAttributeResponse, SchemaAttributeCreate } from '@/types/api/document-schemas';
import { useToast } from '@/hooks/use-toast';
import type { SortDescriptor } from 'react-aria-components';

export const DocumentTypeSchemasPage = () => {
    const { documentTypeId } = useParams<{ documentTypeId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const toast = useToast();
    const [searchTerm, setSearchTerm] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<boolean | ''>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSchema, setEditingSchema] = useState<DocumentSchemaResponse | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [schemaToDelete, setSchemaToDelete] = useState<string | null>(null);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [schemaToDuplicate, setSchemaToDuplicate] = useState<DocumentSchemaResponse | null>(null);
    const [duplicateFormData, setDuplicateFormData] = useState({ schema_name: '', schema_code: '' });
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: 'schema_name',
        direction: 'ascending',
    });

    // Fetch document type details from UUID
    const { data: documentType, isLoading: isLoadingDocType, isError: isErrorDocType } = useDocumentType(documentTypeId || '');

    // Fetch schemas filtered by document_type (key)
    const { data: allSchemas = [], isLoading: isLoadingSchemas, isError: isErrorSchemas } = useDocumentSchemas(
        {
            limit: 500,
            document_type: documentType?.key,
            region: regionFilter || undefined,
            is_active: statusFilter !== '' ? statusFilter : undefined,
        },
        {
            enabled: !!documentType?.key, // Only fetch when we have the document type key
        }
    );

    // Mutations
    const createSchema = useCreateDocumentSchema();
    const updateSchema = useUpdateDocumentSchema();
    const deleteSchema = useDeleteDocumentSchema();
    const addAttributeToSchema = useAddAttributeToSchema();

    const [formData, setFormData] = useState<DocumentSchemaCreate>({
        document_type: documentType?.key || '',
        schema_name: '',
        schema_code: '',
        version: '1.0',
        description: '',
        use_cases: '',
        region: '',
        country_codes: [],
        carrier_codes: [],
        is_default: false,
        is_active: true,
    });

    // Update formData when documentType changes
    useMemo(() => {
        if (documentType?.key && !editingSchema) {
            setFormData(prev => ({
                ...prev,
                document_type: documentType.key,
            }));
        }
    }, [documentType?.key, editingSchema]);

    const regionOptions = [
        { id: '', label: t('schemas.regionPlaceholder') },
        { id: 'asia', label: 'Asia' },
        { id: 'europe', label: 'Europa' },
        { id: 'americas', label: 'Americhe' },
        { id: 'africa', label: 'Africa' },
        { id: 'middle_east', label: 'Medio Oriente' },
    ];

    const statusOptions = [
        { id: '', label: t('schemas.statusPlaceholder') },
        { id: 'true', label: t('schemas.status.active') },
        { id: 'false', label: t('schemas.status.inactive') },
    ];

    // Filter schemas by search term (client-side filtering)
    const filteredSchemas = useMemo(() => {
        if (!searchTerm) return allSchemas;
        const term = searchTerm.toLowerCase();
        return allSchemas.filter(
            (schema) =>
                schema.schema_name.toLowerCase().includes(term) ||
                schema.schema_code.toLowerCase().includes(term) ||
                schema.description?.toLowerCase().includes(term) ||
                schema.use_cases?.toLowerCase().includes(term)
        );
    }, [allSchemas, searchTerm]);

    // Sort the filtered results
    const schemas = useMemo(() => {
        if (!sortDescriptor.column) return filteredSchemas;

        return [...filteredSchemas].sort((a, b) => {
            const column = sortDescriptor.column as keyof DocumentSchemaResponse;
            let aValue = a[column];
            let bValue = b[column];

            // Handle null/undefined values
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';

            // Compare values
            let comparison = 0;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                comparison = aValue - bValue;
            } else {
                comparison = String(aValue).localeCompare(String(bValue));
            }

            return sortDescriptor.direction === 'descending' ? -comparison : comparison;
        });
    }, [filteredSchemas, sortDescriptor]);

    const handleOpenModal = (schema?: DocumentSchemaResponse) => {
        if (schema) {
            setEditingSchema(schema);
            setFormData({
                document_type: schema.document_type,
                schema_name: schema.schema_name,
                schema_code: schema.schema_code,
                version: schema.version,
                description: schema.description || '',
                use_cases: schema.use_cases || '',
                region: schema.region || '',
                country_codes: schema.country_codes || [],
                carrier_codes: schema.carrier_codes || [],
                is_default: schema.is_default,
                is_active: schema.is_active,
            });
        } else {
            setEditingSchema(null);
            setFormData({
                document_type: documentType?.key || '',
                schema_name: '',
                schema_code: '',
                version: '1.0',
                description: '',
                use_cases: '',
                region: '',
                country_codes: [],
                carrier_codes: [],
                is_default: false,
                is_active: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSchema(null);
    };

    const handleSave = async () => {
        try {
            if (editingSchema) {
                const updateData: DocumentSchemaUpdate = {
                    schema_name: formData.schema_name,
                    description: formData.description || null,
                    use_cases: formData.use_cases || null,
                    region: formData.region || null,
                    country_codes: formData.country_codes && formData.country_codes.length > 0 ? formData.country_codes : null,
                    carrier_codes: formData.carrier_codes && formData.carrier_codes.length > 0 ? formData.carrier_codes : null,
                    is_default: formData.is_default,
                    is_active: formData.is_active,
                    version: formData.version || null,
                };
                await updateSchema.mutateAsync({
                    schemaId: editingSchema.document_schema_id,
                    data: updateData,
                });
                toast.success(t('schemas.messages.updateSuccess', { defaultValue: 'Schema aggiornato con successo' }));
            } else {
                // Auto-generate schema_code from schema_name if not provided
                const finalFormData: DocumentSchemaCreate = {
                    // If document_type_id is available, use it instead of document_type (backend prefers UUID)
                    ...(documentType?.document_type_id
                        ? { document_type_id: documentType.document_type_id }
                        : { document_type: formData.document_type }
                    ),
                    schema_name: formData.schema_name,
                    schema_code: formData.schema_code,
                    version: formData.version && formData.version.trim() ? formData.version.trim() : undefined,
                    // Normalize optional string fields: convert empty strings to null
                    description: formData.description && formData.description.trim() ? formData.description.trim() : null,
                    use_cases: formData.use_cases && formData.use_cases.trim() ? formData.use_cases.trim() : null,
                    region: formData.region && formData.region.trim() ? formData.region.trim() : null,
                    // Normalize arrays: convert empty arrays to null (backend accepts null or array)
                    country_codes: formData.country_codes && formData.country_codes.length > 0 ? formData.country_codes : null,
                    carrier_codes: formData.carrier_codes && formData.carrier_codes.length > 0 ? formData.carrier_codes : null,
                    is_default: formData.is_default,
                    is_active: formData.is_active,
                };
                if (!finalFormData.schema_code && finalFormData.schema_name) {
                    finalFormData.schema_code = finalFormData.schema_name
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^_+|_+$/g, '');
                }
                await createSchema.mutateAsync(finalFormData);
                toast.success(t('schemas.messages.createSuccess', { defaultValue: 'Schema creato con successo' }));
            }
            handleCloseModal();
        } catch (error) {
            console.error('Error saving schema:', error);
            toast.error(t('schemas.messages.saveError', { defaultValue: 'Errore nel salvataggio dello schema' }));
        }
    };

    const handleDeleteClick = (id: string) => {
        setSchemaToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!schemaToDelete) return;

        try {
            await deleteSchema.mutateAsync(schemaToDelete);
            toast.success(t('schemas.messages.deleteSuccess', { defaultValue: 'Schema eliminato con successo' }));
            setIsDeleteModalOpen(false);
            setSchemaToDelete(null);
        } catch (error) {
            console.error('Error deleting schema:', error);
            toast.error(t('schemas.messages.deleteError', { defaultValue: 'Errore nell\'eliminazione dello schema' }));
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setSchemaToDelete(null);
    };

    const handleOpenDuplicateModal = (schema: DocumentSchemaResponse) => {
        setSchemaToDuplicate(schema);
        setDuplicateFormData({ schema_name: '', schema_code: '' });
        setIsDuplicateModalOpen(true);
    };

    const handleCloseDuplicateModal = () => {
        setIsDuplicateModalOpen(false);
        setSchemaToDuplicate(null);
        setDuplicateFormData({ schema_name: '', schema_code: '' });
    };

    const handleDuplicateConfirm = async () => {
        if (!schemaToDuplicate || !duplicateFormData.schema_name.trim() || !duplicateFormData.schema_code.trim()) {
            toast.error(t('schemas.messages.duplicateValidation', { defaultValue: 'Inserisci nome e codice per il nuovo schema' }));
            return;
        }
        try {
            const createPayload: DocumentSchemaCreate = {
                ...(documentType?.document_type_id
                    ? { document_type_id: documentType.document_type_id }
                    : { document_type: schemaToDuplicate.document_type }
                ),
                schema_name: duplicateFormData.schema_name.trim(),
                schema_code: duplicateFormData.schema_code.trim(),
                version: schemaToDuplicate.version || '1.0',
                description: schemaToDuplicate.description ?? undefined,
                use_cases: schemaToDuplicate.use_cases ?? undefined,
                region: schemaToDuplicate.region ?? undefined,
                country_codes: schemaToDuplicate.country_codes ?? undefined,
                carrier_codes: schemaToDuplicate.carrier_codes ?? undefined,
                is_default: false,
                is_active: schemaToDuplicate.is_active,
            };
            const newSchema = await createSchema.mutateAsync(createPayload);
            const sourceAttributes = await apiGet<SchemaAttributeResponse[]>(
                `/document-schemas/${schemaToDuplicate.document_schema_id}/attributes`
            );
            let skippedAttrs = 0;
            for (const attr of sourceAttributes) {
                const attrData: SchemaAttributeCreate = {
                    document_attribute_id: attr.document_attribute_id,
                    importance_level: attr.importance_level,
                    is_required: attr.is_required,
                    display_order: attr.display_order,
                    custom_label: attr.custom_label ?? undefined,
                    custom_help_text: attr.custom_help_text ?? undefined,
                    custom_validation_rules: attr.custom_validation_rules ?? undefined,
                    default_value: attr.default_value ?? undefined,
                    attribute_metadata: attr.attribute_metadata ?? undefined,
                };
                try {
                    await addAttributeToSchema.mutateAsync({ schemaId: newSchema.document_schema_id, data: attrData });
                } catch {
                    skippedAttrs++;
                    console.warn(`Skipped attribute ${attr.attribute_key || attr.document_attribute_id} during duplication`);
                }
            }
            toast.success(t('schemas.messages.duplicateSuccess', { defaultValue: 'Schema duplicato con successo' }));
            handleCloseDuplicateModal();
        } catch (error) {
            console.error('Error duplicating schema:', error);
            toast.error(t('schemas.messages.duplicateError', { defaultValue: 'Errore nella duplicazione dello schema' }));
        }
    };

    const isLoading = isLoadingDocType || isLoadingSchemas;
    const isError = isErrorDocType || isErrorSchemas;

    if (isLoading || !documentTypeId || !documentType) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-error mb-4">{t('schemas.messages.loadError', { defaultValue: 'Errore nel caricamento degli schemi' })}</p>
                    <Button onClick={() => window.location.reload()} size="md" color="primary">
                        {t('common.retry', { defaultValue: 'Riprova' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link
                    to="/admin/documents/types"
                    className="text-tertiary hover:text-primary"
                >
                    {t('breadcrumb.documentTypes')}
                </Link>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{documentType.name}</span>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{t('breadcrumb.schemas')}</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">
                        {t('schemas.title')} - {documentType.name}
                    </h2>
                    <p className="text-tertiary mt-1">{t('schemas.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('schemas.newSchema')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('schemas.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('schemas.regionPlaceholder')}
                        items={regionOptions}
                        selectedKey={regionFilter}
                        onSelectionChange={(key) => setRegionFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('schemas.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter !== '' ? String(statusFilter) : ''}
                        onSelectionChange={(key) => setStatusFilter(key === '' ? '' : key === 'true')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Schemas Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('schemas.title')} (${schemas.length})`}
                    description={t('schemas.description')}
                />

                <Table sortDescriptor={sortDescriptor} onSortChange={setSortDescriptor}>
                    <Table.Header>
                        <Table.Head id="schema_name" label={t('schemas.headers.schemaName')} isRowHeader allowsSorting />
                        <Table.Head id="schema_code" label={t('schemas.headers.schemaCode')} />
                        <Table.Head id="region" label={t('schemas.headers.region')} />
                        <Table.Head id="attribute_count" label={t('schemas.headers.attributeCount')} allowsSorting />
                        <Table.Head label={t('schemas.headers.isDefault')} />
                        <Table.Head label={t('schemas.headers.isActive')} />
                        <Table.Head id="created_at" label={t('schemas.headers.createdAt')} allowsSorting />
                        <Table.Head label={t('schemas.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {schemas.map((schema) => (
                            <Table.Row key={schema.document_schema_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{schema.schema_name}</div>
                                    {schema.description && (
                                        <div className="text-sm text-tertiary">{schema.description}</div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary font-mono">{schema.schema_code}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{schema.region || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{schema.attribute_count}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    {schema.is_default ? (
                                        <Badge color="brand" size="sm">{t('schemas.status.default')}</Badge>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {schema.is_active ? (
                                        <Badge color="success" size="sm">{t('schemas.status.active')}</Badge>
                                    ) : (
                                        <Badge color="error" size="sm">{t('schemas.status.inactive')}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {schema.created_at ? new Date(schema.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <Dropdown.Root>
                                        <Dropdown.DotsButton />
                                        <Dropdown.Popover className="w-min">
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    id="viewAttributes"
                                                    icon={FileCheck02}
                                                    label={t('schemas.actions.viewAttributes')}
                                                    onAction={() => {
                                                        navigate(`/admin/documents/types/${documentTypeId}/schemas/${schema.document_schema_id}/attributes`);
                                                    }}
                                                />
                                                <Dropdown.Item
                                                    id="parserRules"
                                                    icon={FileCode01}
                                                    label={t('schemas.actions.parserRules')}
                                                    onAction={() => {
                                                        navigate(`/admin/documents/types/${documentTypeId}/schemas/${schema.document_schema_id}/parser-rules`);
                                                    }}
                                                />
                                                <Dropdown.Item
                                                    id="edit"
                                                    icon={Edit01}
                                                    label={t('schemas.actions.edit')}
                                                    onAction={() => handleOpenModal(schema)}
                                                />
                                                <Dropdown.Item
                                                    id="duplicate"
                                                    icon={Copy01}
                                                    label={t('schemas.actions.duplicate')}
                                                    onAction={() => handleOpenDuplicateModal(schema)}
                                                />
                                                <Dropdown.Separator />
                                                <Dropdown.Item
                                                    id="delete"
                                                    icon={Trash01}
                                                    label={t('schemas.actions.delete')}
                                                    onAction={() => handleDeleteClick(schema.document_schema_id)}
                                                />
                                            </Dropdown.Menu>
                                        </Dropdown.Popover>
                                    </Dropdown.Root>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {schemas.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingSchema ? t('schemas.modal.edit') : t('schemas.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="text-sm font-medium text-primary mb-1 block">
                                                {t('schemas.modal.fields.documentType')}
                                            </label>
                                            <div className="text-primary">{documentType.name}</div>
                                        </div>

                                        <Input
                                            label={t('schemas.modal.fields.version')}
                                            placeholder={t('schemas.modal.fields.versionPlaceholder')}
                                            value={formData.version}
                                            onChange={(value) => setFormData({ ...formData, version: value })}
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('schemas.modal.fields.schemaName')}
                                        placeholder={t('schemas.modal.fields.schemaNamePlaceholder')}
                                        value={formData.schema_name}
                                        onChange={(value) => {
                                            if (!editingSchema) {
                                                // Generate schema code automatically when creating new schema
                                                const code = value
                                                    .toLowerCase()
                                                    .replace(/[^a-z0-9]+/g, '_')
                                                    .replace(/^_+|_+$/g, '');
                                                setFormData((prev) => ({ ...prev, schema_name: value, schema_code: code }));
                                            } else {
                                                setFormData((prev) => ({ ...prev, schema_name: value }));
                                            }
                                        }}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.schemaCode')}
                                        placeholder={t('schemas.modal.fields.schemaCodePlaceholder')}
                                        value={formData.schema_code}
                                        onChange={(value) => setFormData({ ...formData, schema_code: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.description')}
                                        placeholder={t('schemas.modal.fields.descriptionPlaceholder')}
                                        value={formData.description || ''}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.useCases')}
                                        placeholder={t('schemas.modal.fields.useCasesPlaceholder')}
                                        value={formData.use_cases || ''}
                                        onChange={(value) => setFormData({ ...formData, use_cases: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Select
                                            label={t('schemas.modal.fields.region')}
                                            items={regionOptions.filter((opt) => opt.id !== '')}
                                            selectedKey={formData.region || ''}
                                            onSelectionChange={(key) => setFormData({ ...formData, region: key as string })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Input
                                            label={t('schemas.modal.fields.countryCodes')}
                                            placeholder={t('schemas.modal.fields.countryCodesPlaceholder')}
                                            value={formData.country_codes?.join(', ') || ''}
                                            onChange={(value) =>
                                                setFormData({
                                                    ...formData,
                                                    country_codes: value.split(',').map((c) => c.trim()).filter(Boolean),
                                                })
                                            }
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('schemas.modal.fields.carrierCodes')}
                                        placeholder={t('schemas.modal.fields.carrierCodesPlaceholder')}
                                        value={formData.carrier_codes?.join(', ') || ''}
                                        onChange={(value) =>
                                            setFormData({
                                                ...formData,
                                                carrier_codes: value.split(',').map((c) => c.trim()).filter(Boolean),
                                            })
                                        }
                                        size="md"
                                    />

                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_default || false}
                                                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <span className="text-sm text-primary">{t('schemas.modal.fields.isDefault')}</span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active !== undefined ? formData.is_active : true}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <span className="text-sm text-primary">{t('schemas.modal.fields.isActive')}</span>
                                        </label>
                                    </div>
                                </div>

                                {(createSchema.isPending || updateSchema.isPending) && (
                                    <div className="mt-4 text-sm text-tertiary">
                                        {t('schemas.messages.saving', { defaultValue: 'Salvataggio...' })}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={handleCloseModal}
                                        size="md"
                                        color="secondary"
                                        isDisabled={createSchema.isPending || updateSchema.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleSave}
                                        size="md"
                                        color="primary"
                                        isDisabled={createSchema.isPending || updateSchema.isPending}
                                    >
                                        {editingSchema ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Duplicate Schema Modal */}
            <DialogTrigger isOpen={isDuplicateModalOpen} onOpenChange={(open) => !open && handleCloseDuplicateModal()}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('schemas.modal.duplicateTitle', { defaultValue: 'Duplica schema' })}
                                </h2>
                                <p className="text-tertiary mb-4">
                                    {t('schemas.modal.duplicateDescription', {
                                        defaultValue: 'Inserisci nome e codice per il nuovo schema. Il resto delle informazioni e gli attributi saranno copiati dallo schema sorgente.'
                                    })}
                                </p>
                                <div className="space-y-4 mb-6">
                                    <Input
                                        label={t('schemas.modal.fields.schemaName')}
                                        placeholder={t('schemas.modal.fields.schemaNamePlaceholder')}
                                        value={duplicateFormData.schema_name}
                                        onChange={(value) => setDuplicateFormData((prev) => ({ ...prev, schema_name: value }))}
                                        isRequired
                                        size="md"
                                    />
                                    <Input
                                        label={t('schemas.modal.fields.schemaCode')}
                                        placeholder={t('schemas.modal.fields.schemaCodePlaceholder')}
                                        value={duplicateFormData.schema_code}
                                        onChange={(value) => setDuplicateFormData((prev) => ({ ...prev, schema_code: value }))}
                                        isRequired
                                        size="md"
                                    />
                                </div>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={handleCloseDuplicateModal}
                                        size="md"
                                        color="secondary"
                                        isDisabled={createSchema.isPending || addAttributeToSchema.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleDuplicateConfirm}
                                        size="md"
                                        color="primary"
                                        isDisabled={
                                            !duplicateFormData.schema_name.trim() ||
                                            !duplicateFormData.schema_code.trim() ||
                                            createSchema.isPending ||
                                            addAttributeToSchema.isPending
                                        }
                                    >
                                        {createSchema.isPending || addAttributeToSchema.isPending
                                            ? t('schemas.messages.duplicating', { defaultValue: 'Duplicazione...' })
                                            : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={handleDeleteCancel}
                                        size="md"
                                        color="secondary"
                                        isDisabled={deleteSchema.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleDeleteConfirm}
                                        size="md"
                                        color="primary-destructive"
                                        isDisabled={deleteSchema.isPending}
                                    >
                                        {deleteSchema.isPending ? t('loading') : t('modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
